import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// --- Data & Services ---
import { getLocationBySlug, getLocationsData } from '../../Public/locations-data';
import SystemDataService from '../../Services/SystemDataService';

// --- Layout & Global UI ---
import Layout from '../../Utils/Navbar/navbar-layout-component.js';
import Footer from '../../Utils/Footer/footer-component.js';
import NotFound from '../Error-Screen/NotFound-screen';
import LazyOnView from '../../Utils/LazyLoading/LazyOnView.js';

// --- Reusable Design System Sections ---
import LocationsHeroSection from './Locations-Hero-Section.js';
import MetricsSection from '../../Utils/Sections/section-metrics-component.js';
import PricingSection from '../../Utils/Sections/section-pricing-component.js';
import FaqSection from '../../Utils/Sections/section-faq-component.js';
import InfoCardsSection from '../../Utils/Sections/section-info-cards-component.js';
import Contact from '../Contact-screen/Contact.js';
import ContentSection from '../../Utils/Content/content-section-component.js';
import LocationsMiddleSection from './Locations-middle-section.js';
import LocationsSection from './LocationsSection.js';
import ServicesSection from '../Services-screen/ServicesSection.js';
import TypesSection from '../../Utils/Sections/section-types-component.js';
import QuickCallSection from '../../Utils/Sections/section-quick-call-component.js';

const Location = () => {
    const { locationSlug } = useParams();
    const [loading, setLoading] = useState(true);
    const [matchedLocation, setMatchedLocation] = useState(null);
    const [otherLocations, setOtherLocations] = useState([]);

    useEffect(() => {
        const initialize = async () => {
            setLoading(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });

            try {
                // Parallel fetching for performance
                const [data, allLocations] = await Promise.all([
                    getLocationBySlug(locationSlug),
                    getLocationsData()
                ]);

                if (data) {
                    setMatchedLocation(data);

                    // Filter and map other locations for the bottom slider
                    const filtered = allLocations
                        .filter(loc => loc.slug !== locationSlug)
                        .map(loc => ({
                            ...loc,
                            title: loc.name,
                            cardImage: loc.image,
                            actionHref: `/locations/${loc.slug}`,
                            btnText: 'View Location'
                        }));
                    setOtherLocations(filtered);
                }
            } catch (err) {
                console.error("Critical error initializing location screen:", err);
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, [locationSlug]);

    if (loading) return <div className="min-h-screen animate-pulse bg-white dark:bg-neutral-950" />;
    if (!matchedLocation) return <NotFound />;

    return (
        <div className="flex w-full flex-col bg-white dark:bg-neutral-950 font-assistant">
            <Layout seo={matchedLocation.seo}>
                <Helmet>
                    <script type="application/ld+json">
                        {JSON.stringify(matchedLocation.schema)}
                    </script>
                </Helmet>

                {/* --- 1. HERO SECTION (Map discovery + Main Card) --- */}
                {/* Passing full location object for deep component access */}
                <LocationsHeroSection data={matchedLocation} />

                {/* --- 2. METRICS STRIP --- */}
                <div className="w-full bg-primary-800 text-white border-y border-neutral-200 dark:border-white/5">
                    <MetricsSection metrics={matchedLocation.atAGlance} />
                </div>

                {/* --- 3. MIDDLE SECTION (Insights & Case Study) --- */}
                <LocationsMiddleSection
                    insightsData={matchedLocation.localInsights}
                    caseStudyData={matchedLocation.caseStudy}
                />


                {/* --- 4. EDITORIAL CONTENT (Deep Dive) --- */}
                {matchedLocation.intro && (
                    <div className='flex py-16'>
                        <ContentSection
                            title={matchedLocation.intro.title}
                            backgroundColor="bg-white"
                            buttonText="Get Your Free Estimate"
                        >
                            {matchedLocation.intro.content}
                        </ContentSection>
                    </div>
                )}

                {/* --- 6. PRICING & LOCAL RATES --- */}
                {matchedLocation.pricing && (
                    <PricingSection pricingData={matchedLocation.pricing} />
                )}

                <QuickCallSection/>

                {/* --- 5. PROCESS & SERVICES TIPS --- */}
                {matchedLocation.process && (
                    <TypesSection data={matchedLocation.process} />
                )}

                {/* --- 9. TRUST ASSETS & CONTACT --- */}
                <LazyOnView threshold={0.1}>
                    <InfoCardsSection />
                </LazyOnView>


                {/* --- 8. GLOBAL SERVICE NETWORK ---
                <Services
                    title="Explore Other Service Areas"
                    subTitle={`We proudly serve ${matchedLocation.name} and the surrounding regions.`}
                    servicesData={otherLocations}
                    isLearnMore={true}
                /> */}
                <ServicesSection
                    isHeadline={true} // Set to true to trigger the vertical structural line if needed
                    limit={4}
                    showMore={false}
                    showMoreText="View All Systems"
                    loading={false}
                />

                {/* --- 8. GLOBAL SERVICE NETWORK (Other Locations) --- */}
                <div className="w-full">
                    <LocationsSection
                        title={`Beyond ${matchedLocation.name}`}
                        isHeadline={true} // Triggers the "Regional Coverage" header
                        locations={otherLocations} // Uses the filtered list from your state
                        limit={3} // Keeps the page length clean
                        showMore={true} // Triggers the "View All Areas" button at the bottom
                    />
                </div>

                {/* --- 7. FAQ ENGINE --- */}
                {matchedLocation.faqList && (
                    <div className="bg-neutral-50 dark:bg-neutral-900">
                        <div className="max-w-7xl mx-auto">
                            <FaqSection
                                faqCards={matchedLocation.faqList}
                            />
                        </div>
                    </div>
                )}

                <div id="contact" className="w-full">
                    <Contact />
                </div>

            </Layout>
            <Footer />
        </div>
    );
};

export default Location;